/**
 * v-lazy-youtube directive
 *
 * Post-processes v-html content:
 *   1. Replaces YouTube iframes with lightweight thumbnail facades.
 *      Clicking the facade opens the video in a full-size lightbox
 *      modal — this keeps lesson cards compact while giving users the
 *      full player (volume, CC, settings, fullscreen — all visible).
 *   2. Ensures every other embedded iframe (Loom, Vimeo, etc.) supports
 *      fullscreen by adding `allowfullscreen` + `fullscreen` in `allow`.
 *   3. Ensures <video> elements have `controls` and open in the same
 *      lightbox on click.
 *
 * Usage:  <div v-lazy-youtube v-html="richContent"></div>
 */

const YT_REGEX = /(?:youtube\.com\/embed\/|youtube-nocookie\.com\/embed\/)([a-zA-Z0-9_-]{11})/;

const IFRAME_ALLOW =
  "accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture";

const PLAY_SVG = `<svg width="56" height="56" viewBox="0 0 68 48" style="filter:drop-shadow(0 2px 8px rgba(0,0,0,.3))">
  <path d="M66.52 7.74c-.78-2.93-2.49-5.41-5.42-6.19C55.79.13 34 0 34 0S12.21.13 6.9 1.55C3.97 2.33 2.27 4.81 1.48 7.74.06 13.05 0 24 0 24s.06 10.95 1.48 16.26c.78 2.93 2.49 5.41 5.42 6.19C12.21 47.87 34 48 34 48s21.79-.13 27.1-1.55c2.93-.78 4.64-3.26 5.42-6.19C67.94 34.95 68 24 68 24s-.06-10.95-1.48-16.26z" fill="red"/>
  <path d="M45 24L27 14v20" fill="white"/>
</svg>`;

const CLOSE_SVG = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>`;

let styleInjected = false;
function injectStyles() {
  if (styleInjected) return;
  styleInjected = true;
  const style = document.createElement("style");
  style.textContent = `
    .video-lightbox-backdrop {
      position: fixed; inset: 0; z-index: 3000;
      background: rgba(0, 0, 0, 0.88);
      display: flex; align-items: center; justify-content: center;
      padding: 24px;
      animation: video-lightbox-fade 0.15s ease-out;
    }
    @keyframes video-lightbox-fade {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
    .video-lightbox-frame {
      position: relative;
      width: min(90vw, calc((90vh - 40px) * 16 / 9));
      aspect-ratio: 16 / 9;
      background: #000;
      border-radius: 10px;
      overflow: hidden;
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
    }
    .video-lightbox-frame > iframe,
    .video-lightbox-frame > video {
      width: 100%; height: 100%; border: none; display: block; background: #000;
    }
    .video-lightbox-close {
      position: absolute; top: -44px; right: 0;
      width: 36px; height: 36px;
      display: flex; align-items: center; justify-content: center;
      background: rgba(255, 255, 255, 0.15);
      color: #fff; border: none; border-radius: 50%;
      cursor: pointer; transition: background 0.15s;
    }
    .video-lightbox-close:hover { background: rgba(255, 255, 255, 0.25); }
  `;
  document.head.appendChild(style);
}

function openLightbox(buildMedia) {
  injectStyles();

  const backdrop = document.createElement("div");
  backdrop.className = "video-lightbox-backdrop";

  const frame = document.createElement("div");
  frame.className = "video-lightbox-frame";

  const closeBtn = document.createElement("button");
  closeBtn.className = "video-lightbox-close";
  closeBtn.setAttribute("aria-label", "Close");
  closeBtn.innerHTML = CLOSE_SVG;

  frame.appendChild(closeBtn);
  frame.appendChild(buildMedia());
  backdrop.appendChild(frame);
  document.body.appendChild(backdrop);

  const prevOverflow = document.body.style.overflow;
  document.body.style.overflow = "hidden";

  const close = () => {
    document.body.style.overflow = prevOverflow;
    document.removeEventListener("keydown", onKey);
    backdrop.remove();
  };
  const onKey = (e) => {
    if (e.key === "Escape") close();
  };

  backdrop.addEventListener("click", (e) => {
    if (e.target === backdrop) close();
  });
  closeBtn.addEventListener("click", close);
  document.addEventListener("keydown", onKey);
}

function enableFullscreen(iframe) {
  iframe.setAttribute("allowfullscreen", "");
  iframe.setAttribute("webkitallowfullscreen", "");
  iframe.setAttribute("mozallowfullscreen", "");
  const existing = iframe.getAttribute("allow") || "";
  if (!/\bfullscreen\b/.test(existing)) {
    iframe.setAttribute("allow", existing ? `${existing}; fullscreen` : IFRAME_ALLOW);
  }
}

function buildYoutubeIframe(videoId) {
  const iframe = document.createElement("iframe");
  iframe.src = `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1`;
  iframe.setAttribute("allow", IFRAME_ALLOW);
  enableFullscreen(iframe);
  return iframe;
}

function buildGenericIframe(src) {
  const iframe = document.createElement("iframe");
  iframe.src = src;
  iframe.setAttribute("allow", IFRAME_ALLOW);
  enableFullscreen(iframe);
  return iframe;
}

function buildVideoClone(source) {
  const clone = document.createElement("video");
  clone.src = source.currentSrc || source.src;
  clone.setAttribute("controls", "");
  clone.setAttribute("autoplay", "");
  clone.setAttribute("playsinline", "");
  return clone;
}

function replaceIframes(el) {
  const ytIframes = el.querySelectorAll('iframe[src*="youtube"]:not([data-lazy-yt])');

  ytIframes.forEach((iframe) => {
    const match = iframe.src.match(YT_REGEX);
    if (!match) return;
    const videoId = match[1];

    const wrapper = document.createElement("div");
    wrapper.className = "lazy-yt-facade";
    wrapper.setAttribute("data-video-id", videoId);
    wrapper.innerHTML = `
      <img
        src="https://i.ytimg.com/vi/${videoId}/hqdefault.jpg"
        alt="Video"
        loading="lazy"
      />
      <div class="lazy-yt-facade__overlay">${PLAY_SVG}</div>
    `;

    wrapper.addEventListener("click", () => {
      openLightbox(() => buildYoutubeIframe(videoId));
    });

    iframe.parentNode.replaceChild(wrapper, iframe);
  });

  el.querySelectorAll("iframe:not([data-lazy-yt])").forEach((iframe) => {
    enableFullscreen(iframe);
    if (iframe.dataset.lightboxBound) return;
    iframe.dataset.lightboxBound = "true";
    iframe.style.cursor = "zoom-in";
    iframe.addEventListener("click", (e) => {
      e.preventDefault();
      const src = iframe.src;
      openLightbox(() => buildGenericIframe(src));
    });
  });

  el.querySelectorAll("video").forEach((video) => {
    if (!video.hasAttribute("controls")) video.setAttribute("controls", "");
    video.setAttribute("controlslist", "nodownload");
    video.setAttribute("playsinline", "");
    if (video.dataset.lightboxBound) return;
    video.dataset.lightboxBound = "true";
    video.addEventListener("click", (e) => {
      const rect = video.getBoundingClientRect();
      if (e.clientY > rect.bottom - 40) return;
      e.preventDefault();
      video.pause();
      openLightbox(() => buildVideoClone(video));
    });
  });
}

export const vLazyYoutube = {
  mounted(el) {
    replaceIframes(el);
  },
  updated(el) {
    requestAnimationFrame(() => replaceIframes(el));
  },
};
